import { useEffect, useState } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import axios from 'axios';
import PersonasModal from '../../../../General/Components/Catalogos/Personas/PersonasModal';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const EMPTY = {
  idPersona: 0,
  nombreUsuario: '',
  contrasenaHASH: '',
  salt: '',
  ultimoAcceso: '',
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idPersona: '',
    nombreUsuario: '',
    contrasenaHASH: '',
  });

  const [rowsPersonas, setPersonas] = useState([]);
  const [rowsUsuarios, setUsuarios] = useState([]);
  const [personasOpen, setPersonasOpen] = useState(false);
  const [savingPersona, setSavingPersona] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setAlertConfig({ open: true, message, variant, title });
  };

  // Valores originales (solo para edición)
  const [originalValues, setOriginalValues] = useState({
    idPersona: 0,
    nombreUsuario: '',
  });

  // Cuando true, persona y nombreUsuario quedan bloqueados
  const [lockUserFields] = useState(false);

  // -------- VALIDACIONES --------
  const validateField = (field, rawValue) => {
    const value =
      field === 'idPersona'
        ? Number(rawValue) || 0
        : (rawValue || '').trim();

    let error = '';

    switch (field) {
      case 'idPersona':
        if (!value || value <= 0) {
          error = 'Selecciona una persona.';
        }
        break;

      case 'nombreUsuario':
        if (!value) {
          error = 'El nombre de usuario es obligatorio.';
        } else {
          // Validar duplicidad
          const low = value.toLowerCase();
          const duplicate = rowsUsuarios.some(u => 
            u.nombreUsuario?.trim().toLowerCase() === low &&
            (mode === 'new' || u.idUsuario !== data?.idUsuario)
          );
          if (duplicate) {
            error = 'Este nombre de usuario ya está registrado. Elige otro.';
          }
        }
        break;

      case 'contrasenaHASH':
        // Para nuevo usuario es obligatoria
        if (mode === 'new' && !value) {
          error = 'La contraseña es obligatoria para usuarios nuevos.';
        } else if (value && value.length < 6) {
          // Regla mínima común: si se ingresa, que tenga al menos 6 caracteres
          error = 'La contraseña debe tener al menos 6 caracteres.';
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const okPersona = validateField('idPersona', form.idPersona);
    const okNombre = validateField('nombreUsuario', form.nombreUsuario);
    const okPass = validateField('contrasenaHASH', form.contrasenaHASH);
    return okPersona && okNombre && okPass;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'idPersona' || field === 'nombreUsuario') {
      validateField(field, value);
    }
  };

  const handlePasswordChange = (value) => {
    const newValue = value;

    if (mode === 'edit') {
      const hasValue = newValue.trim().length > 0;

      if (hasValue) {
        setForm((prev) => ({
          ...prev,
          contrasenaHASH: newValue,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          contrasenaHASH: '',
        }));
      }
    } else {
      // Nuevo usuario: solo actualizamos
      setForm((prev) => ({ ...prev, contrasenaHASH: newValue }));
    }

    // Validar contraseña para ambos modos
    validateField('contrasenaHASH', newValue);
  };

  const handleSubmit = () => {
    const isValid = validateAll();
    if (!isValid) return;
    onSubmit(buildPayload(), mode);
  };

  // -------- EFECTOS --------
  useEffect(() => {
    if (mode === 'edit' && data) {
      const idPersona = data.idPersona ?? 0;
      const nombreUsuario = data.nombreUsuario ?? '';

      setForm({
        idPersona,
        nombreUsuario,
        contrasenaHASH: '',
        salt: '',
        fechaAlta: data.fechaAlta,
        ultimoAcceso: data.ultimoAcceso ?? '',
      });

      setOriginalValues({
        idPersona,
        nombreUsuario,
      });

      setErrors({
        idPersona:
          idPersona && idPersona > 0 ? '' : 'Selecciona una persona.',
        nombreUsuario:
          (nombreUsuario || '').trim()
            ? ''
            : 'El nombre de usuario es obligatorio.',
        contrasenaHASH: '',
      });
    } else {
      setForm(EMPTY);
      setOriginalValues({ idPersona: 0, nombreUsuario: '' });
      setErrors({
        idPersona: '',
        nombreUsuario: '',
        contrasenaHASH: '',
      });
    }
  }, [mode, data]);

  const loadPersonas = async () => {
    try {
      const [resPersonas, resUsuarios] = await Promise.all([
        axios.get(ENDPOINTS.personas),
        axios.get(ENDPOINTS.usuarios),
      ]);
      setPersonas((resPersonas.data || []).map((x) => Models.personas(x)));
      setUsuarios((resUsuarios.data || []).map((x) => Models.usuarios(x)));
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al obtener la información necesaria. Inténtelo más tarde.');
    }
  };

  useEffect(() => {
    if (open) loadPersonas();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filtrar personas que ya tienen usuario asignado (excepto la persona del usuario actual en modo edición)
  const personasDisponibles = rowsPersonas.filter((persona) => {
    // En modo edición, permitir la persona actual
    if (mode === 'edit' && persona.idPersona === originalValues.idPersona) {
      return true;
    }
    // Excluir personas que ya tienen un usuario
    const yaAsignada = rowsUsuarios.some((u) => u.idPersona === persona.idPersona);
    return !yaAsignada;
  });

  if (!open) return null;

  // -------- BUILD PAYLOAD (contraseña con flujo especial) --------
  const buildPayload = () => {
    const payload = { ...form };

    // En edición, si la contraseña está vacía, no se envía ni contraseña ni salt
    if (!payload.contrasenaHASH?.trim() && mode !== 'new') {
      delete payload.contrasenaHASH;
      delete payload.salt;
    }

    payload.idPersona = Number(payload.idPersona) || 0;
    return payload;
  };

  // -------- NUEVA PERSONA DESDE MODAL --------
  const handleSubmitNuevaPersona = async (personaForm) => {
    try {
      setSavingPersona(true);
      const res = await axios.post(ENDPOINTS.personas, personaForm);
      const created = res?.data || {};

      // Refrescar catálogo
      await loadPersonas();

      // Extraer el ID de forma robusta
      let newId = 0;
      if (typeof created === 'number') {
        newId = created;
      } else if (typeof created === 'object' && created !== null) {
        newId = created.idPersona ?? created.IdPersona ?? created.id ?? created.Id ?? 0;
      }

      if (newId && newId > 0) {
        // Usar setTimeout para asegurar que el select se haya actualizado con las nuevas opciones
        setTimeout(() => {
          setForm((f) => ({ ...f, idPersona: newId }));
          validateField('idPersona', newId);
        }, 100);
      }
      setPersonasOpen(false);
    } catch (e) {
      console.error(e);
      triggerAlert('No se pudo crear la persona. Inténtelo más tarde.');
    } finally {
      setSavingPersona(false);
    }
  };

  // -------- UI --------
  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const passwordRequiredOK =
    mode === 'new'
      ? (form.contrasenaHASH || '').trim()
      : true;

  const allRequiredFilled =
    Number(form.idPersona) > 0 &&
    (form.nombreUsuario || '').trim() &&
    passwordRequiredOK;

  const hasErrors = Object.values(errors).some((e) => !!e);

  const isSaveDisabled = !allRequiredFilled || hasErrors;

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo usuario' : 'Editar usuario'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Persona */}
      <div>
        <label
          className="roles-title"
          style={{ fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 8 }}
        >
          Persona:
          <button
            type="button"
            className="btn-action"
            onClick={() => setPersonasOpen(true)}
            title="Crear nueva persona"
            style={{ padding: '2px 8px', lineHeight: 1 }}
            disabled={lockUserFields}
          >
            ＋ Persona
          </button>
        </label>
        <br /><br />
        <select
          className={`infoField${errors.idPersona ? ' infoField--error' : ''}`}
          value={form.idPersona}
          onChange={e => handleChange('idPersona', e.target.value)}
        >
          <option value={0}>— Selecciona persona —</option>
          {personasDisponibles.map((persona) => {
            const fullName = `${persona.nombre || ''} ${persona.apellidoPaterno || ''} ${persona.apellidoMaterno || ''}`.trim();
            return (
              <option key={persona.idPersona} value={persona.idPersona}>
                {fullName}
              </option>
            );
          })}
        </select>
        {errors.idPersona && <small style={errorStyle}>{errors.idPersona}</small>}
      </div>

      {/* Nombre de usuario */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre de Usuario:</label>
        <br /><br />
        <input
          className={`infoField${errors.nombreUsuario ? ' infoField--error' : ''}`}
          value={form.nombreUsuario}
          onChange={(e) => handleChange('nombreUsuario', e.target.value)}
          maxLength={100}
        />
        {errors.nombreUsuario && <small style={errorStyle}>{errors.nombreUsuario}</small>}
      </div>

      {/* Contraseña */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>
          Contraseña {mode === 'new' ? '(obligatoria para nuevo usuario)' : '(Solo llenar para actualizar contraseña)'}
        </label>
        <br /><br />
        <div style={{ position: 'relative' }}>
          <input
            className={`infoField${errors.contrasenaHASH ? ' infoField--error' : ''}`}
            type={showPassword ? 'text' : 'password'}
            value={form.contrasenaHASH}
            onChange={(e) => handlePasswordChange(e.target.value)}
            maxLength={256}
            style={{ paddingRight: '2.5rem' }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 4,
            }}
            title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
          >
            {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
          </button>
        </div>
        {errors.contrasenaHASH && <small style={errorStyle}>{errors.contrasenaHASH}</small>}
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="btn-cancel" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={handleSubmit}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}

        <PersonasModal
          open={personasOpen}
          mode="new"
          onClose={() => setPersonasOpen(false)}
          onSubmit={handleSubmitNuevaPersona}
        />
        {savingPersona && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
        )}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>

      <PersonasModal
        open={personasOpen}
        mode="new"
        onClose={() => setPersonasOpen(false)}
        onSubmit={handleSubmitNuevaPersona}
      />
      {savingPersona && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
      )}
      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
